import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { StaysApiError } from '../../../../api/stays-client';
import { reissueCheckinLink } from '../../../../api/stays-checkin-client';
import {
  ComposeResult,
  composeMessage,
  listMessageTemplates,
  MessageLanguage,
  MessageTemplate,
} from '../../../../api/stays-messages-client';
import type { StayFull } from '../../../../interfaces/stays';
import { AppButton } from '../../../button/app-button';
import { CountedTextarea } from '../../../counted-textarea/counted-textarea';
import { HelperText } from '../../../form-kit';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { FormModal } from '../../../modal/form-modal';
import { ChipGroup, ToggleChip } from '../../../toggle-chip/toggle-chip';
import { SegmentedControl } from '../../../segmented-control/segmented-control';
import { InlineMessage, StateSwitch } from '../../../../motion';
import toast from '../../../../utils/toast';
import { phoneLinks } from '../../../../utils/stays-phone-links';
import { MESSAGE_MAX } from '../../../../utils/stays-template-placeholders';
import { ListSkeleton, LoadError } from '../../dashboard/dashboard-shared-components/load-state';
import { refusalText } from '../../dashboard/dashboard-stays-components/stays-labels';
import { spacing } from '../../../../style-utils/theme';

/**
 * THE MESSAGE COMPOSER (plan S8, SC-4; R-S8.21 … R-S8.23). Pick a template;
 * the SERVER fills it for this stay (door codes only once S7's gate has
 * released them) and stores nothing. The host may edit the text, then copy it
 * or open WhatsApp / Viber on their own device. Lobbero sends nothing.
 *
 * The links follow the text: an edit rebuilds them here with the same rule
 * the server uses (utils/stays-phone-links.ts, one cases file). WhatsApp opens
 * with window.open from a button (S6's checkin-card pattern), never a button
 * nested in a link.
 */

export const LANGUAGE_OPTIONS: ReadonlyArray<{ value: MessageLanguage; label: string }> = [
  { value: 'en', label: 'English' },
  { value: 'pt', label: 'Português' },
  { value: 'es', label: 'Español' },
];

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  min-width: 0;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};
  justify-content: flex-end;
`;

export const MessageComposer: React.FC<{ stay: StayFull | null; onClose: () => void }> = ({ stay, onClose }) => {
  const [language, setLanguage] = useState<MessageLanguage>('en');
  const [category, setCategory] = useState<string>('All');
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [view, setView] = useState<'loading' | 'error' | 'ready'>('loading');
  const [chosen, setChosen] = useState<string | null>(null);
  const [result, setResult] = useState<ComposeResult | null>(null);
  const [text, setText] = useState('');
  const [composing, setComposing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [askRotate, setAskRotate] = useState(false);
  const seq = useRef(0);
  const first = stay ? stay.lead.first_name || 'the guest' : '';

  const loadLibrary = useCallback(async () => {
    setView('loading');
    try {
      const lib = await listMessageTemplates(language);
      setTemplates(lib.templates);
      setView('ready');
    } catch {
      setView('error');
    }
  }, [language]);

  useEffect(() => {
    if (stay) void loadLibrary();
  }, [stay, loadLibrary]);

  useEffect(() => {
    setChosen(null);
    setResult(null);
    setText('');
    setCategory('All');
  }, [stay, language]);

  const compose = useCallback(
    async (templateId: string) => {
      if (!stay) return;
      const mine = ++seq.current;
      setComposing(true);
      try {
        const r = await composeMessage(stay.id, { template_id: templateId });
        if (mine !== seq.current) return;
        setResult(r);
        setText(r.text);
      } catch (e) {
        if (mine === seq.current) toast.error(refusalText(e as StaysApiError, 'Could not prepare the message.'));
      } finally {
        if (mine === seq.current) setComposing(false);
      }
    },
    [stay]
  );

  const categories = useMemo(() => ['All', ...Array.from(new Set(templates.map(t => t.category)))], [templates]);
  const visible = templates.filter(t => category === 'All' || t.category === category);
  const links = result ? phoneLinks(result.phone.digits ? `+${result.phone.digits}` : null, text).links : null;
  const edited = !!result && text !== result.text;

  const copy = async (): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);

      return true;
    } catch {
      toast.error('Could not copy. Select the text and copy it yourself.');

      return false;
    }
  };

  const rotate = async () => {
    if (!stay || !chosen) return;
    try {
      await reissueCheckinLink(stay.id);
      setAskRotate(false);
      await compose(chosen);
    } catch (e) {
      toast.error(refusalText(e as StaysApiError, 'Could not make a new link.'));
    }
  };

  return (
    <FormModal
      open={!!stay}
      title={`Message ${first}`}
      onClose={onClose}
      isDirty={edited}
      footer={
        <Actions>
          <AppButton type="button" variant="secondary" disabled={!text} success={copied} successLabel="Copied"
            onClick={() => void copy()}>
            Copy message
          </AppButton>
          {links ? (
            <>
              <AppButton
                type="button"
                variant="secondary"
                disabled={!text}
                onClick={async () => {
                  if (await copy()) window.location.href = links.viber;
                }}
              >
                Copy and open Viber
              </AppButton>
              <AppButton type="button" variant="primary" disabled={!text} data-href={links.whatsapp}
                onClick={() => window.open(links.whatsapp, '_blank', 'noopener,noreferrer')}>
                Open in WhatsApp
              </AppButton>
            </>
          ) : null}
        </Actions>
      }
    >
      <Stack>
        <SegmentedControl<MessageLanguage> label="Language" options={LANGUAGE_OPTIONS} value={language} onChange={setLanguage} />
        <StateSwitch state={view}>
          {view === 'loading' ? <ListSkeleton rows={3} rowHeight={48} label="templates" /> : null}
          {view === 'error' ? <LoadError message="Couldn't load the message templates" onRetry={() => void loadLibrary()} /> : null}
          {view === 'ready' ? (
            <Stack>
              <ChipGroup aria-label="Category">
                {categories.map(c => (
                  <ToggleChip key={c} pressed={category === c} onToggle={() => setCategory(c)}>
                    {c}
                  </ToggleChip>
                ))}
              </ChipGroup>
              <ChipGroup aria-label="Template">
                {visible.map(t => (
                  <ToggleChip
                    key={t.id}
                    pressed={chosen === t.id}
                    onToggle={() => {
                      setChosen(t.id);
                      void compose(t.id);
                    }}
                  >
                    {t.title}
                  </ToggleChip>
                ))}
              </ChipGroup>
            </Stack>
          ) : null}
        </StateSwitch>
        <CountedTextarea
          label="Message"
          value={text}
          max={MESSAGE_MAX}
          rows={8}
          aria-busy={composing || undefined}
          placeholder={chosen ? '' : 'Choose a template'}
          onChange={e => setText(e.target.value)}
        />
        {(result ? result.warnings : []).map(w => (
          <InlineMessage key={`${w.code}-${w.placeholder || ''}`} role="status" message={w.message} />
        ))}
        {result && result.checkin_link.state === 'rotate_needed' ? (
          <AppButton type="button" variant="ghost" onClick={() => setAskRotate(true)}>
            Insert a new check-in link
          </AppButton>
        ) : null}
        <HelperText>Messages are sent from your own phone. Lobbero does not send or keep them.</HelperText>
      </Stack>
      <ConfirmDialog
        open={askRotate}
        title="Make a new check-in link?"
        message="This replaces the link you sent before. The old link stops working."
        primaryLabel="Make a new link"
        onPrimary={rotate}
        onDismiss={() => setAskRotate(false)}
      />
    </FormModal>
  );
};
